import React, { useEffect, useMemo } from 'react'
import MicroModal from "micromodal"

import {
  ColumnDef,
  SortingState
} from '@tanstack/react-table'

import CourseDetails from '../../Table/Modal/CourseDetails'
import TableCheckbox from '../../../../common/checkbox/TableCheckbox'
import { CourseReportData, CourseReport } from './data/CourseReportData'
import CourseUserDetails from '../../Table/Modal/CourseUserDetails'
import { useCourseReport } from '../context/CourseReportContext'
import TanstackContent from '../../Table/Tanstack/TanstackContent'
import { getExportHeaders } from '../types/tableHeaders'
import UserQuiz from '../../Table/Modal/UserQuiz'
import Button from '../../../../common/button/Button'

const CourseReport = ({props, teamData, siteData}) => {
  const { setSelectedCourse, setGroupReportSnapshot, datePicker, setTeamData, setSiteOption } = useCourseReport()
  props.datePicker = datePicker

  const [rowSelection, setRowSelection] = React.useState({})
  const [globalFilter, setGlobalFilter] = React.useState('')
  const [sorting, setSorting] = React.useState<SortingState>([])
  const [data, setData] = React.useState(() => CourseReportData(props, teamData ))
  
  useEffect(()=>{
    setGroupReportSnapshot(props)
    setTeamData( teamData )
    setSiteOption( siteData )
  },[])

  useEffect(()=>{
    setData( () => CourseReportData( props, teamData ))
  }, [ datePicker ])

  MicroModal.init()

  const columns = React.useMemo<ColumnDef<CourseReport>[]>(
    () => [
      {
        id: 'select',
        header: ({ table }) => (
          <TableCheckbox
            {...{
              checked: table.getIsAllRowsSelected(),
              indeterminate: table.getIsSomeRowsSelected(),
              onChange: table.getToggleAllRowsSelectedHandler(),
            }}
          />
        ),
        cell: ({ row }) => (
          <div className="px-1">
            <TableCheckbox
              {...{
                checked: row.getIsSelected(),
                disabled: !row.getCanSelect(),
                indeterminate: row.getIsSomeSelected(),
                onChange: row.getToggleSelectedHandler(),
              }}
            />
          </div>
        ),
      },
      {
        id: 'columns',
        columns: [
          {
            id: 'courseId',
            accessorKey: 'courseId',
            prettyName: 'Course ID',
            show: false,
            cell: info => info.getValue(),
            header: () => <span>COURSE ID</span>,
          },
          {
            id: 'course',
            accessorKey: 'course',
            prettyName: 'Course',
            cell: info => info.getValue(),
            footer: props => props.column.id,
            header: () => <span>COURSE</span>,
          },
          {
            id: 'enrolled',
            accessorKey: 'enrolled',
            prettyName: 'Enrolled',
            cell: info => info.getValue(),
            footer: props => props.column.id,
            header: () => <span>ENROLLED</span>,
          },
          {
            id: 'completed',
            accessorKey: 'completed',
            prettyName: 'Completed',
            cell: info => info.getValue(),
            footer: props => props.column.id,
            header: () => <span>COMPLETED</span>,
          },
          { 
            id: 'inProgress',
            accessorKey: 'inProgress',
            prettyName: 'In Progress',
            cell: info => info.getValue(),
            footer: props => props.column.id,
            header: () => <span>IN PROGRESS</span>,
          },
          { 
            id: 'notStarted',
            accessorKey: 'notStarted',
            prettyName: 'Not Started',
            cell: info => info.getValue(),
            footer: props => props.column.id,
            header: () => <span>NOT STARTED</span>,
          },
          { 
            id: 'viewDetails',
            accessorKey: 'viewDetails',
            prettyName: 'Action',
            cell: (info) => (
              <ViewDetails info={info.row.original} />
            ),
            footer: props => props.column.id,
            header: () => <span>ACTIONS</span>,
          },
        ],
      },
    ],
    []
  )

  const exportHeaders = getExportHeaders( 'course-report' )

  const handleOpenModal = (info) => {
    setSelectedCourse(info)
    MicroModal.show(`msync-course-report-course-container`)
  }

  const ViewDetails = ({info}) => {
    const isDisabled = !props.users

    return (
      <Button
        name='View Details'
        className='msync-button--is-size-sm msync-button--is-theme-secondary msync-button--is-style-outline'
        onClick={() => handleOpenModal(info)} disabled={ isDisabled } 
      />
    )
  }
  
  return (
    <TanstackContent
      data={data}
      columns={columns}
      rowSelection={rowSelection}
      setRowSelection={setRowSelection}
      sorting={sorting}
      setSorting={setSorting}
      globalFilter={globalFilter}
      setGlobalFilter={setGlobalFilter}
      exportHeaders={exportHeaders}
      from={'Course Report'}
      context={'Courses'}
      columnFilters={ null }
      setColumnFilters={ null }
    >
      <CourseDetails data={props.users} />
      <UserQuiz from={'course-report'} />
      <CourseUserDetails />
    </TanstackContent>
  )
}

export default CourseReport

